import React, { useState, useEffect, useReducer, useCallback, useMemo, lazy, Suspense } from 'react';
import { Edit2, ChevronDown, ChevronRight, Sparkles, Image } from 'lucide-react';
import ChunkLoadErrorBoundary from './ChunkLoadErrorBoundary';
import MobileModal from './MobileModal';
import { RECHARGE_TYPES } from '../data/rechargeTypes';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumAccess } from '../hooks/usePremiumAccess';
import { useCharacter } from '../contexts/CharacterContext';
import { systemBus, EVENTS } from '../services/systemBus';
// [BATCH 11 / P1 D-28 — APR 2026]: Canonical spell datasets. Pre-fix the
//   dialog had a hardcoded SPELL_LIST const with ~120 spells, levels 0-5
//   only. Now queries srdSpells (343 spells) + sourcesManager (Tasha's,
//   Xanathar's, Fizban's, custom CW spells) so the magic-item authoring
//   surface matches what casters actually have in their books.
import { SRD_SPELLS } from '../data/srdSpells';
import { sourcesManager } from '../data/sourcesManager';
// [SUB-PLAN 1R-eid — 2026-05-29]: EQUIPMENT_SLOT_LABELS removed — the equip-slot
//   field now uses <ReferencePicker kind="equipSlot"> (sibling of 1T Stone 2).
//   Registry reads EQUIPMENT_SLOT_LABELS internally via composerCatalogRegistry.js.
//   Both dialogs now share the same 26-slot composer kind, ensuring parity
//   without each maintaining its own inline iteration.
import ReferencePicker from './composer/ReferencePicker';
// [PHASE B / #064 — 2026-05-07]: Same WEAPON_PROPERTIES data block ItemDialog
//   uses (`#030`). Picker iterates this array so descriptions / ids / labels
//   stay in lockstep across both dialogs. Closes B1-4 EditItemDialog parity gap.
import { WEAPON_PROPERTIES } from '../data/weaponProperties';
// [AUDIT #100 / STONE 7 follow-up — 2026-05-18]: AI item-icon callback writes
//   to playerStats.imageLibrary instead of legacy character.imageGallery.
import { addImage, imagesByEntity } from '../utils/imageLibrary';
import { resolveCurrentCharges, formatChargeFieldValue } from '../utils/chargedItemDefaults'; // [SUB-PLAN 1C ITEM 1 / 2026-05-23]: Charge field load + save defaults
// [PHASE B / #026 — 2026-05-06]: Items become the 6th member of the CW
//   authoring quintet (Race / Class / Subclass / Spell / Feat) by
//   importing the same FeatureEffectsSection. The section is purely
//   presentational — all add/remove/update callbacks are bound below
//   to the local `effects` state. This wraps the existing item-specific
//   UI (equipSlot, weapon properties, charges, attunement, weight,
//   value) without disrupting it; the effects[] N-array is additive
//   alongside the legacy per-ability `statModifications` grid.
import {
  FeatureEffectsSection,
  emptyEffect,
} from './effectBuilder/EffectBuilderControls';
// [STONES 5-6 — 2026-05-26]: Wire canonical S1 effects array at save time so
//   buildItemRegistrationsFromInventory → effectRegistry picks up ALL user-authored
//   passive effects (ability mods, resistances, senses, speed). Rule 31 trace
//   at .claude/findings/2026-05-26-datamodel-item-effects-4e7d12.md confirms
//   that EditItemDialog items with ability mods were silently broken before
//   this commit (S4 flat-key shape never reached path 1). Now fixed.
import { buildEffectsFromForm } from '../utils/itemEffectsRegistration';
// [SUB-PLAN 1H-K — 2026-05-29]: Per-instance customChoices resolver.
// resolveCustomChoiceEffects(itemDef, instanceChoices) → effects[] for items
// with a customChoices config (e.g. ring_resistance: player picks damage type).
import { resolveCustomChoiceEffects } from '../utils/itemCustomChoices';
// [SUB-PLAN 1H-K — 2026-05-29]: getItemDefinition to look up the BASE_ITEMS
// row (and its customChoices config) from the inventory item's .type key.
import { getItemDefinition } from '../data/gameData';

// [v1.0]: Lazy-load AI generator (only needed when user clicks "Generate Icon")
const AIImageGenerator = lazy(() => import('./AIImageGenerator'));
// [v1.1]: Lazy-load Image Gallery
const ImageGallery = lazy(() => import('./ImageGallery'));

const EditItemDialog = ({
  editingItem,
  setEditingItem,
  // [PHASE 4B - PARTIAL MIGRATION]: Keeping function props for Phase 4B Step 6
  updateItem
}) => {
  // [PHASE 4B - MIGRATED]: Now using context hooks instead of props
  const { settings } = useSettings();
  const { isPremium: authIsPremium } = useAuth();
  const hasPremium = usePremiumAccess();
  const { playerStats: character, setPlayerStats: setCharacter, logActivity } = useCharacter();
  // [v1.1]: Gallery state
  const [showGallery, setShowGallery] = useState(false);

  // [BATCH 11 / P1 D-28 — APR 2026]: Build a level-keyed dropdown map
  //   from the canonical SRD_SPELLS dataset (level-keyed) merged with
  //   active-source spells (name-keyed via sourcesManager). De-dupe by
  //   name; sort each level alphabetically. Empty try/catch so unit
  //   tests that don't initialize sourcesManager still render.
  const spellsByLevel = useMemo(() => {
    const byLevel = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [], 9: [] };
    Object.entries(SRD_SPELLS).forEach(([levelKey, spells]) => {
      const lvl = parseInt(levelKey, 10);
      if (!Array.isArray(spells) || !byLevel[lvl]) return;
      spells.forEach(s => {
        if (s && s.name) byLevel[lvl].push({ name: s.name, level: lvl });
      });
    });
    try {
      const sourceSpells = sourcesManager.getAllSpells() || {};
      Object.values(sourceSpells).forEach(s => {
        if (!s || !s.name) return;
        const lvl = typeof s.level === 'number' ? s.level : 0;
        if (!byLevel[lvl]) return;
        if (byLevel[lvl].find(x => x.name === s.name)) return;
        byLevel[lvl].push({ name: s.name, level: lvl });
      });
    } catch (e) { /* sources not initialized — fall back to SRD only */ }
    Object.keys(byLevel).forEach(lvl => byLevel[lvl].sort((a, b) => a.name.localeCompare(b.name)));
    return byLevel;
  }, []);


  // Initialize with actual item values when available
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [weight, setWeight] = useState('');
  const [value, setValue] = useState('');
  const [charges, setCharges] = useState('');
  const [maxCharges, setMaxCharges] = useState('');
  const [customImage, setCustomImage] = useState('');
  // [P2.13 APR 29 2026]: Stack quantity input for stackable items.
  // [WHY]: User reported daggers (2) couldn't be increased or decreased
  //   via the editor — the dialog had no quantity field at all. Floor
  //   at 1 (use the existing Remove flow to delete a stack outright).
  const [quantity, setQuantity] = useState(1);
  // [PASS-D APR 30 2026]: Per-item stackable toggle. Override the
  //   BASE_ITEMS default (false for weapons/armor, true for ammo/consumables)
  //   on a per-instance basis. Flipping ON exposes the quantity editor
  //   above; flipping OFF hides it. Persisted on the inventory item.
  // [FIX MAY 2026]: Moved out of standalone "Stack Behavior" fieldset into
  //   Advanced Properties section per user request — the prior dedicated
  //   fieldset was too prominent for what's effectively an opt-in toggle.
  const [stackable, setStackable] = useState(false);
  // [FIX MAY 2026]: Max stack size — per-item cap for how many can fit in
  //   one stack. Defaults to BASE_ITEMS value (e.g. arrows: 20, potions: 99)
  //   or empty (unbounded) when missing. Editable from Advanced Properties.
  const [maxStack, setMaxStack] = useState('');
  // [v1.0]: AI icon generator toggle
  const [showAIGenerator, setShowAIGenerator] = useState(false);

  // Container-specific state
  const [containerWidth, setContainerWidth] = useState(4);
  const [containerHeight, setContainerHeight] = useState(4);

  // [D.3 APR 2026]: Item's own grid footprint (e.g., greatsword = 1×3 cells).
  // Editable per-item; default to 1×1 if item has no size or size is invalid.
  const [itemWidth, setItemWidth] = useState(1);
  const [itemHeight, setItemHeight] = useState(1);
  
  // Advanced properties state
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [material, setMaterial] = useState('');
  const [magicBonus, setMagicBonus] = useState('');
  const [damageDice, setDamageDice] = useState('');
  const [damageType, setDamageType] = useState('');
  const [selectedSpell, setSelectedSpell] = useState('');
  const [spellCharges, setSpellCharges] = useState('');
  const [spellRecharge, setSpellRecharge] = useState('dawn');
  const [requiresAttunement, setRequiresAttunement] = useState(false);
  // [BATCH 11 / P1 D-28 — APR 2026]: Resolve the picked spell's level
  //   so the saved `spells[0].level` reflects the actual spell, not a
  //   hardcoded 1. Pre-fix Fireball saved as level-1 (slot consumption
  //   wrong), Cure Wounds-as-cantrip saved as level-1 (also wrong).
  //   Note: braces around the early return keep the awk-based Rule 7
  //   anti-pattern check (which flags `if (...) return null;` on a
  //   single line at indent 2-4) from misreading a callback-internal
  //   early return as a component-level early return before hooks.
  const selectedSpellData = useMemo(() => {
    let resolved = null;
    if (selectedSpell) {
      for (const lvlKey of Object.keys(spellsByLevel)) {
        const found = spellsByLevel[lvlKey].find(s => s.name === selectedSpell);
        if (found) {
          resolved = found; // { name, level }
          break;
        }
      }
    }
    return resolved;
  }, [selectedSpell, spellsByLevel]);
  const [equipSlot, setEquipSlot] = useState('');
  const [statBonus, setStatBonus] = useState('');
  const [statValue, setStatValue] = useState('');
  const [rechargeType, setRechargeType] = useState(RECHARGE_TYPES.NONE);

  // [PHASE 10 - NOV 20, 2025]: RESTORED REMOVED FEATURES - D&D 5e item properties
  // [WHY]: These properties existed in old system, characters have items using them
  // [CRITICAL]: Cannot recreate SRD items without these properties (user requirement)
  const [rarity, setRarity] = useState('');
  const [attunementPrerequisite, setAttunementPrerequisite] = useState('');
  const [isCursed, setIsCursed] = useState(false);
  const [curseDescription, setCurseDescription] = useState('');
  const [resistances, setResistances] = useState([]);
  const [immunities, setImmunities] = useState([]);
  const [weaponProperties, setWeaponProperties] = useState([]);
  const [normalRange, setNormalRange] = useState('');
  const [longRange, setLongRange] = useState('');
  const [speedBonus, setSpeedBonus] = useState('');
  const [flyingSpeed, setFlyingSpeed] = useState('');
  const [swimmingSpeed, setSwimmingSpeed] = useState('');
  const [skillBonuses, setSkillBonuses] = useState([]);
  const [savingThrowBonuses, setSavingThrowBonuses] = useState([]);
  const [conditionImmunities, setConditionImmunities] = useState([]);
  const [darkvision, setDarkvision] = useState('');
  const [blindsight, setBlindsight] = useState('');
  const [tremorsense, setTremorsense] = useState('');
  const [advantageOn, setAdvantageOn] = useState([]);

  // [ENHANCED]: Multi-stat modification state
  // [WHY]: Support multiple stat modifications per item (e.g., Cloak of Protection: +1 DEX, +1 AC)
  // [STRUCTURE]: Each ability has enabled flag, effect type (set/add), value, and optional max ceiling.
  // [SUB-PLAN 1H-H-maxcap-authoring — 2026-05-30]: Added `max` field to each row.
  //   The `max` field is only meaningful for 'add' operations — it declares an ability-score
  //   ceiling (e.g., Ioun Stone: "+2 DEX to a maximum of 20"). Set operations target a specific
  //   value and have no ceiling concept. The field is null by default (no ceiling).
  const [statMods, setStatMods] = useState({
    strength: { enabled: false, type: 'set', value: '', max: null },
    dexterity: { enabled: false, type: 'set', value: '', max: null },
    constitution: { enabled: false, type: 'set', value: '', max: null },
    intelligence: { enabled: false, type: 'set', value: '', max: null },
    wisdom: { enabled: false, type: 'set', value: '', max: null },
    charisma: { enabled: false, type: 'set', value: '', max: null }
  });

  // [SUB-PLAN 1N — 2026-05-27]: HP/AC/Initiative/Saves form fields
  //   Write to canonical S1 effects[] only — NOT to statModifications S4 keyed-object.
  const [bonusHP, setBonusHP] = useState('');
  const [acBonus, setAcBonus] = useState('');
  const [initiativeBonus, setInitiativeBonus] = useState('');
  const [saves, setSaves] = useState({});

  // [PHASE B / #026 — 2026-05-06]: N-array of structured effects bound
  //   to FeatureEffectsSection. Same shape as the CW creator quintet's
  //   effects authoring (criticalRange / damageBonus / proficiency /
  //   sense / advantage / resistance / etc. — see
  //   `src/data/subclassEffectBuilderVocab.js`). On save, this array
  //   merges into `updates.effects` alongside the existing
  //   resistance/immunity bridging entries (lines below in handleSave),
  //   so itemEffectsRegistration sees BOTH the legacy resistances/
  //   immunities/skillBonuses and the new authored effects when the
  //   item is equipped.
  const [effects, setEffects] = useState([]);

  // [SUB-PLAN 1H-K — 2026-05-29]: Per-instance customChoices state.
  // Mirrors the feat customChoices pattern. Persisted on the item instance.
  // Only rendered when the BASE_ITEMS def has a customChoices config.
  const [customChoices, setCustomChoices] = useState({});

  // [PHASE 5 SESSION 3]: Force re-render mechanism for reactive item updates
  // [PURPOSE]: Enables dialog to react to item editing events from other sources
  // [WHY]: When items are edited elsewhere (equipment panel, vault, containers), this dialog needs to refresh
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 5 SESSION 3]: Event subscriptions for reactive edit item dialog
  // [PURPOSE]: Subscribe to item editing and equipment changes for real-time updates
  // [WHY]: This dialog may be open when items are edited from:
  //   - Equipment panel quick edits
  //   - Container item modifications
  //   - Vault item updates
  //   - Item imports/exports
  // [INTEGRATION]: Ensures item properties stay synchronized across all interfaces
  useEffect(() => {
    const handleItemEvent = (eventName, data) => {
      // Force re-render to show updated item properties
      forceUpdate();
    };

    // [ITEM_EDITED]: Item properties were edited (from any source)
    const unsubscribeEdited = systemBus.on(EVENTS.ITEM_EDITED, (data) =>
      handleItemEvent('ITEM_EDITED', data));

    // [EQUIPMENT_CHANGED]: Equipment was equipped/unequipped (affects item state)
    const unsubscribeEquipment = systemBus.on(EVENTS.EQUIPMENT_CHANGED, (data) =>
      handleItemEvent('EQUIPMENT_CHANGED', data));

    return () => {
      unsubscribeEdited();
      unsubscribeEquipment();
    };
  }, []);

  // Update state when editingItem changes
  React.useEffect(() => {
    if (editingItem?.item) {
      setName(editingItem.item.name || '');
      setDescription(editingItem.item.description || '');
      setWeight(editingItem.item.weight !== undefined ? editingItem.item.weight : '');
      setValue(editingItem.item.value !== undefined ? editingItem.item.value : '');
      // [SUB-PLAN 1C ITEM 1 / 2026-05-23]: Preserve `0` (consumed state) on load.
      //   Pre-fix `editingItem.item.charges || ''` stripped a legitimate 0 to ''.
      setCharges(formatChargeFieldValue(editingItem.item.charges));
      setMaxCharges(formatChargeFieldValue(editingItem.item.maxCharges));
      setCustomImage(editingItem.item.customImage || '');
      // [P2.13 APR 29 2026]: Load existing stack quantity (default 1 if missing).
      setQuantity(
        Number.isFinite(editingItem.item.quantity) && editingItem.item.quantity > 0
          ? editingItem.item.quantity
          : 1
      );
      // [PASS-D APR 30 2026]: Load existing stackable flag. Defaults false
      //   if the item lacks the property entirely (treats unknown items as
      //   non-stackable).
      setStackable(editingItem.item.stackable === true);
      // [FIX MAY 2026]: Load existing maxStack (per-instance override) if
      //   present, else fall back to BASE_ITEMS value via initial empty.
      setMaxStack(
        Number.isFinite(editingItem.item.maxStack) && editingItem.item.maxStack > 0
          ? String(editingItem.item.maxStack)
          : ''
      );
      setContainerWidth(editingItem.item.containerSlots?.[0] || 4);
      setContainerHeight(editingItem.item.containerSlots?.[1] || 4);
      // [D.3 APR 2026]: Load existing item footprint or default 1×1.
      setItemWidth(Array.isArray(editingItem.item.size) ? (editingItem.item.size[0] || 1) : 1);
      setItemHeight(Array.isArray(editingItem.item.size) ? (editingItem.item.size[1] || 1) : 1);
      setMaterial(editingItem.item.material || '');
      // Support both magicalBonus and magicBonus for backwards compatibility
      setMagicBonus(editingItem.item.magicalBonus || editingItem.item.magicBonus || '');
      setDamageDice(editingItem.item.damageDice || '');
      setDamageType(editingItem.item.damageType || '');
      setSelectedSpell(editingItem.item.grantedSpell || '');
      setSpellCharges(editingItem.item.spellCharges || '');
      setSpellRecharge(editingItem.item.spellRecharge || 'dawn');
      setRequiresAttunement(editingItem.item.requiresAttunement || false);
      setEquipSlot(editingItem.item.equipSlot || '');
      setStatBonus(editingItem.item.statBonus || '');
      setStatValue(editingItem.item.statValue || '');
      setRechargeType(editingItem.item.rechargeType || RECHARGE_TYPES.NONE);

      // [PHASE 10 - NOV 20, 2025]: Load restored D&D 5e properties from item
      setRarity(editingItem.item.rarity || '');
      setAttunementPrerequisite(editingItem.item.attunementPrerequisite || '');
      setIsCursed(editingItem.item.isCursed || false);
      setCurseDescription(editingItem.item.curseDescription || '');
      setResistances(editingItem.item.resistances || []);
      setImmunities(editingItem.item.immunities || []);
      setWeaponProperties(editingItem.item.weaponProperties || []);
      setNormalRange(editingItem.item.normalRange || '');
      setLongRange(editingItem.item.longRange || '');
      setSpeedBonus(editingItem.item.speedBonus || '');
      setFlyingSpeed(editingItem.item.flyingSpeed || '');
      setSwimmingSpeed(editingItem.item.swimmingSpeed || '');
      setSkillBonuses(editingItem.item.skillBonuses || []);
      setSavingThrowBonuses(editingItem.item.savingThrowBonuses || []);
      setConditionImmunities(editingItem.item.conditionImmunities || []);
      setDarkvision(editingItem.item.darkvision || '');
      setBlindsight(editingItem.item.blindsight || '');
      setTremorsense(editingItem.item.tremorsense || '');
      setAdvantageOn(editingItem.item.advantageOn || []);

      // [MIGRATION]: Load stat modifications from new or old format
      // [WHY]: Support both legacy single-stat and new multi-stat format
      // [SUB-PLAN 1H-H-maxcap-authoring — 2026-05-30]: Rows default to max:null (no ceiling).
      //   The max field does not round-trip through the S4 keyed-object shape, so
      //   previously-saved items will show no ceiling on re-edit. This is the correct
      //   backward-compatible behavior (old saves have no ceiling declared).
      const newStatMods = {
        strength: { enabled: false, type: 'set', value: '', max: null },
        dexterity: { enabled: false, type: 'set', value: '', max: null },
        constitution: { enabled: false, type: 'set', value: '', max: null },
        intelligence: { enabled: false, type: 'set', value: '', max: null },
        wisdom: { enabled: false, type: 'set', value: '', max: null },
        charisma: { enabled: false, type: 'set', value: '', max: null }
      };

      // Load from new format (statModifications object)
      if (editingItem.item.statModifications) {
        Object.keys(editingItem.item.statModifications).forEach(key => {
          // Parse keys like "strengthSet", "dexterityAdd"
          const match = key.match(/^(strength|dexterity|constitution|intelligence|wisdom|charisma)(Set|Add)$/i);
          if (match) {
            const ability = match[1].toLowerCase();
            const type = match[2].toLowerCase(); // "set" or "add"
            newStatMods[ability] = {
              enabled: true,
              type: type,
              value: editingItem.item.statModifications[key].toString(),
              max: null, // max does not round-trip through S4 keyed-object; cleared on re-edit
            };
          }
        });
      }
      // Migrate from old format (single statBonus/statValue)
      else if (editingItem.item.statBonus && editingItem.item.statValue) {
        // Parse old format: "setStrength", "addDexterity", etc.
        const match = editingItem.item.statBonus.match(/^(set|add|subtract)(Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma)$/i);
        if (match) {
          const type = match[1].toLowerCase(); // "set", "add", or "subtract"
          const ability = match[2].toLowerCase();
          newStatMods[ability] = {
            enabled: true,
            type: type === 'subtract' ? 'add' : type, // Convert subtract to negative add
            value: type === 'subtract' ?
              (-Math.abs(editingItem.item.statValue)).toString() :
              editingItem.item.statValue.toString(),
            max: null, // max does not exist in legacy format
          };
        }
      }

      setStatMods(newStatMods);

      // [SUB-PLAN 1N — 2026-05-27]: Load HP/AC/initiative/saves from flat fields
      //   (preferred — written by 1N save path) or fall back to scanning effects[].
      //   DO NOT read from statModifications S4 keyed-object for these fields.
      const item1N = editingItem.item;
      // bonusHP
      if (item1N.bonusHP !== undefined && item1N.bonusHP !== null) {
        setBonusHP(String(item1N.bonusHP));
      } else {
        const hpEffect = Array.isArray(item1N.effects) ? item1N.effects.find(e => e.type === 'hp') : null;
        setBonusHP(hpEffect ? String(hpEffect.value) : '');
      }
      // acBonus
      if (item1N.acBonus !== undefined && item1N.acBonus !== null) {
        setAcBonus(String(item1N.acBonus));
      } else {
        const acEffect = Array.isArray(item1N.effects) ? item1N.effects.find(e => e.type === 'ac') : null;
        setAcBonus(acEffect ? String(acEffect.value) : '');
      }
      // initiativeBonus
      if (item1N.initiativeBonus !== undefined && item1N.initiativeBonus !== null) {
        setInitiativeBonus(String(item1N.initiativeBonus));
      } else {
        const initEffect = Array.isArray(item1N.effects) ? item1N.effects.find(e => e.type === 'initiative') : null;
        setInitiativeBonus(initEffect ? String(initEffect.value) : '');
      }
      // saves — flat preferred, then reconstruct from effects[]
      if (item1N.saves && typeof item1N.saves === 'object') {
        setSaves({ ...item1N.saves });
      } else if (Array.isArray(item1N.effects)) {
        const saveRows = item1N.effects.filter(e => e.type === 'save');
        if (saveRows.length > 0) {
          const reconstructed = {};
          const SAVE_SHORT_TO_FULL = { str: 'strength', dex: 'dexterity', con: 'constitution', int: 'intelligence', wis: 'wisdom', cha: 'charisma', all: 'all' };
          saveRows.forEach(row => {
            const fullKey = SAVE_SHORT_TO_FULL[row.target] || row.target;
            reconstructed[fullKey] = row.value;
          });
          setSaves(reconstructed);
        } else {
          setSaves({});
        }
      } else {
        setSaves({});
      }

      // [PHASE B / #026 — 2026-05-06]: Load the item's authored effects
      //   (the N-array authored via FeatureEffectsSection) into local
      //   state. Strip the resistance entries that live alongside the
      //   N-array — those are bridged separately from the resistances/
      //   immunities pickers below, so re-rendering them via the
      //   section would create UI duplicates. The save handler re-adds
      //   the bridge entries on persist, so the persisted shape
      //   stays identical.
      const itemEffects = Array.isArray(editingItem.item.effects)
        ? editingItem.item.effects.filter(e => e?.type !== 'resistance')
        : [];
      setEffects(itemEffects);

      // [SUB-PLAN 1H-K — 2026-05-29]: Load per-instance customChoices from item.
      setCustomChoices(
        editingItem.item.customChoices && typeof editingItem.item.customChoices === 'object'
          ? { ...editingItem.item.customChoices }
          : {}
      );
    }
  }, [editingItem]);
  
  if (!editingItem) return null;

  // [SUB-PLAN 1H-K — 2026-05-29]: Look up the BASE_ITEMS def for this item
  // instance so the customChoices config (and any future per-kind UI gate) is
  // available in the render path. item.type (or .baseType) is the BASE_ITEMS key.
  const _itemType = editingItem.item.type || editingItem.item.baseType;
  const _baseDef = _itemType ? getItemDefinition(_itemType) : null;
  const _baseEntry = _baseDef ? _baseDef.entry : null;

  // [PHASE B / #029 — 2026-05-06]: Cursed Preset handler (B1-3).
  //   Writes a definite shape — not a delta — so applying twice still
  //   yields -1 STR, not -2:
  //     - statMods.strength → enabled, type='add', value='-1'
  //     - isCursed → true (preserves any existing curseDescription)
  //   Author can refine the value, swap the ability, or layer
  //   additional cursed effects via FeatureEffectsSection.
  const applyCursedPreset = () => {
    setStatMods(prev => ({
      ...prev,
      strength: { enabled: true, type: 'add', value: '-1' },
    }));
    setIsCursed(true);
  };

  const handleSave = () => {
    // [ENHANCED]: Convert multi-stat UI state to storage format
    // [WHY]: EffectCalculator expects flat object with keys like "strengthSet", "dexterityAdd"
    // [STONES 5-6 — 2026-05-26]: Also build canonical array for buildEffectsFromForm.
    //   The flat-key object (S4 shape) is kept for round-trip load compatibility
    //   (the regex at L327 parses it back into local statMods state). New saves
    //   additionally carry item.effects (S1) which feeds path 1 correctly.
    const statModifications = {};
    const statModificationsCanonical = [];
    Object.keys(statMods).forEach((ability, i) => {
      const mod = statMods[ability];
      if (mod.enabled && mod.value !== '') {
        const value = parseInt(mod.value);
        if (!isNaN(value)) {
          // Create keys like "strengthSet" or "dexterityAdd" (S4 flat-key, kept for round-trip)
          const key = ability.charAt(0).toUpperCase() + ability.slice(1) +
                      (mod.type === 'set' ? 'Set' : 'Add');
          statModifications[key] = value;
          // Build canonical array for buildEffectsFromForm (S1 target)
          // [SUB-PLAN 1H-H-maxcap-authoring — 2026-05-30]: Include max ceiling when present
          //   and operation is 'add'. This flows to emitAbilityRow → effect.max → effectRegistry
          //   ceiling clamp. Only 'add' rows carry max; 'set' rows do not.
          const smRow = {
            id: `sm_edit_${i}`,
            target: `${mod.type}${ability.charAt(0).toUpperCase() + ability.slice(1)}`,
            value,
          };
          if (mod.type === 'add' && typeof mod.max === 'number' && isFinite(mod.max)) {
            smRow.max = mod.max;
          }
          statModificationsCanonical.push(smRow);
        }
      }
    });

    const updates = {
      name: name || editingItem.item.name,
      description: description !== '' ? description : editingItem.item.description,
      weight: weight !== '' ? parseFloat(weight) : editingItem.item.weight,
      value: value !== '' ? parseFloat(value) : editingItem.item.value,
      // [SUB-PLAN 1C ITEM 1 / 2026-05-23]: Resolve maxCharges first, then default
      //   current = max when user added charges by setting only the Max input.
      //   Mirrors CustomItemCreator.js:508-521 via the shared helper so the
      //   shape is consistent across creators. `resolveCurrentCharges` returns
      //   null when the resolved max ≤ 0 (no-pool semantics), so a user who
      //   types `0` or clears both fields ends up with `{charges:null, maxCharges:null}`
      //   — equivalent to a non-charged item for downstream consumers.
      ...(() => {
        const maxInput = maxCharges !== '' ? parseInt(maxCharges, 10) : editingItem.item.maxCharges;
        const resolvedMax = Number.isFinite(maxInput) && maxInput > 0 ? maxInput : null;
        const currentInputOrFallback = charges !== '' ? charges : (editingItem.item.charges ?? undefined);
        return {
          charges: resolveCurrentCharges(currentInputOrFallback, resolvedMax),
          maxCharges: resolvedMax,
        };
      })(),
      customImage: customImage || editingItem.item.customImage || null,
      // Convert single spell to spells array for magic items
      // [PURPOSE]: Create proper spell structure with uses tracking
      // [WHY]: Magic Items panel needs usesPerDay and currentUses to function
      // [BATCH 11 / P1 D-28 — APR 2026]: Level read from selectedSpellData
      //   (resolved via spellsByLevel against SRD_SPELLS + active sources),
      //   not hardcoded. Required for correct slot-consumption math.
      spells: selectedSpell ? [{
        name: selectedSpell,
        level: selectedSpellData?.level ?? 0,
        usesPerDay: spellCharges ? parseInt(spellCharges) : 1, // Use spell charges input
        currentUses: spellCharges ? parseInt(spellCharges) : 1, // Initialize to max
        dc: null, // No DC by default
        saveType: null // Will be determined when cast
      }] : editingItem.item.spells || [],
      // Advanced properties - preserve existing values if not changed
      material: material !== '' ? material : editingItem.item.material || null,
      magicalBonus: magicBonus !== '' ? parseInt(magicBonus) : (editingItem.item.magicalBonus || editingItem.item.magicBonus || null),
      damageDice: damageDice !== '' ? damageDice : editingItem.item.damageDice || null,
      damageType: damageType !== '' ? damageType : editingItem.item.damageType || null,
      grantedSpell: selectedSpell !== '' ? selectedSpell : editingItem.item.grantedSpell || null,
      spellCharges: spellCharges !== '' ? parseInt(spellCharges) : editingItem.item.spellCharges || null,
      spellRecharge: spellRecharge || editingItem.item.spellRecharge || 'dawn',
      requiresAttunement: requiresAttunement,
      equipSlot: equipSlot !== '' ? equipSlot : editingItem.item.equipSlot || null,
      // [STONES 5-6 — 2026-05-26]: Populate S2 legacy mirrors from the first
      //   canonical row (parallel to ItemDialog Stone-4 pattern, now applied here
      //   too). Pre-fix these were explicitly nulled; now they enable path-3
      //   (InventoryContext.applyItemBonus) for EditItemDialog items during the
      //   transition window before path-3 is deprecated. Rule 31 trace sec (e).
      statBonus: statModificationsCanonical[0]?.target || null,
      statValue: statModificationsCanonical[0]?.value != null ? statModificationsCanonical[0].value : null,
      // [ENHANCED]: New multi-stat modifications format (S4 flat-key kept for round-trip load)
      statModifications: Object.keys(statModifications).length > 0 ? statModifications : null,
      rechargeType: rechargeType || RECHARGE_TYPES.NONE,
      // [PHASE 10]: RESTORED D&D 5e PROPERTIES
      // [WHY]: These properties existed in old system and are required for SRD item recreation
      rarity: rarity || editingItem.item.rarity || null,
      attunementPrerequisite: (requiresAttunement && attunementPrerequisite) ? attunementPrerequisite : editingItem.item.attunementPrerequisite || null,
      isCursed: isCursed || editingItem.item.isCursed || false,
      curseDescription: (isCursed && curseDescription) ? curseDescription : editingItem.item.curseDescription || null,
      resistances: resistances.length > 0 ? resistances : editingItem.item.resistances || null,
      immunities: immunities.length > 0 ? immunities : editingItem.item.immunities || null,
      // [STONES 5-6 — 2026-05-26]: Build canonical S1 effects array via buildEffectsFromForm
      //   so ALL user-authored passive effects reach path 1 (effectRegistry). Replaces
      //   the prior resistance/immunity bridge (fromResistances/fromImmunities used wrong
      //   `value:` key instead of `target:` — buildEffectsFromForm emits the correct
      //   `target:` shape). Rule 31 trace at .claude/findings/2026-05-26-datamodel-item-effects-4e7d12.md.
      //
      //   Merge order (Rule 22 — single source of truth for effect-row assembly):
      //   1. fromSection: FeatureEffectsSection-authored effects (structural/proficiency/
      //      criticalRange/etc.) — kept verbatim; the user typed these explicitly.
      //   2. buildEffectsFromForm: ability mods, resistances, immunities, condition
      //      immunities, senses, speed — canonical shape emitted by the single helper.
      //
      //   The old fromResistances/fromImmunities bridge (Phase-4b APR 2026) is superseded.
      //   Items saved before this commit carry the old `value:` key shape in their
      //   effects array; effectRegistry's resistance reader must tolerate both `value:` and
      //   `target:` keys during the migration window (pre-existing compatibility concern —
      //   out of scope for this commit; tracked in master-queue Stones 5-6 close note).
      // [SUB-PLAN 1N — 2026-05-27]: Mirror flat properties for round-trip load.
      //   NOT for runtime reads (runtime uses effects[] → effectRegistry path-1).
      //   DO NOT add to statModifications S4 keyed-object.
      bonusHP: bonusHP !== '' ? Number(bonusHP) : (editingItem.item.bonusHP || null),
      acBonus: acBonus !== '' ? Number(acBonus) : (editingItem.item.acBonus || null),
      initiativeBonus: initiativeBonus !== '' ? Number(initiativeBonus) : (editingItem.item.initiativeBonus || null),
      saves: Object.keys(saves).length > 0 ? saves : (editingItem.item.saves || null),
      effects: (() => {
        const fromSection = Array.isArray(effects) ? [...effects] : [];
        const fromForm = buildEffectsFromForm({
          statModifications: statModificationsCanonical,
          resistances: resistances.length > 0 ? resistances : (editingItem.item.resistances || []),
          immunities: immunities.length > 0 ? immunities : (editingItem.item.immunities || []),
          conditionImmunities: conditionImmunities.length > 0 ? conditionImmunities : (editingItem.item.conditionImmunities || []),
          darkvision: darkvision ? parseInt(darkvision) : (editingItem.item.darkvision || undefined),
          blindsight: blindsight ? parseInt(blindsight) : (editingItem.item.blindsight || undefined),
          tremorsense: tremorsense ? parseInt(tremorsense) : (editingItem.item.tremorsense || undefined),
          speedBonus: speedBonus ? parseInt(speedBonus) : (editingItem.item.speedBonus || undefined),
          flyingSpeed: flyingSpeed ? parseInt(flyingSpeed) : (editingItem.item.flyingSpeed || undefined),
          swimmingSpeed: swimmingSpeed ? parseInt(swimmingSpeed) : (editingItem.item.swimmingSpeed || undefined),
          advantageOn: advantageOn.length > 0 ? advantageOn : (editingItem.item.advantageOn || []),
          // [SUB-PLAN 1K — 2026-05-27]: Pass new fields to emitter
          bonusHP: bonusHP !== '' ? bonusHP : undefined,
          acBonus: acBonus !== '' ? acBonus : undefined,
          initiativeBonus: initiativeBonus !== '' ? initiativeBonus : undefined,
          saves,
        });
        // [SUB-PLAN 1H-K — 2026-05-29]: Materialize resolved customChoices effects.
        // Resolve the BASE_ITEMS config + current per-instance picks → resistance.grant rows.
        // These are appended alongside fromForm so buildItemRegistrationsFromInventory
        // picks them up via the existing bridge (unchanged — still reads item.effects[]).
        const itemType = editingItem.item.type || editingItem.item.baseType;
        const baseDef = itemType ? getItemDefinition(itemType) : null;
        const fromCustomChoices = baseDef
          ? resolveCustomChoiceEffects(baseDef.entry, customChoices)
          : [];
        const merged = [...fromSection, ...fromForm, ...fromCustomChoices];
        return merged.length > 0 ? merged : null;
      })(),
      // [SUB-PLAN 1H-K — 2026-05-29]: Persist per-instance picks.
      // customChoices null when empty (no picks made) to keep save-file lean.
      customChoices: Object.keys(customChoices).length > 0 ? { ...customChoices } : null,
      weaponProperties: weaponProperties.length > 0 ? weaponProperties : editingItem.item.weaponProperties || null,
      normalRange: normalRange ? parseInt(normalRange) : editingItem.item.normalRange || null,
      longRange: longRange ? parseInt(longRange) : editingItem.item.longRange || null,
      speedBonus: speedBonus ? parseInt(speedBonus) : editingItem.item.speedBonus || null,
      flyingSpeed: flyingSpeed ? parseInt(flyingSpeed) : editingItem.item.flyingSpeed || null,
      swimmingSpeed: swimmingSpeed ? parseInt(swimmingSpeed) : editingItem.item.swimmingSpeed || null,
      skillBonuses: skillBonuses.length > 0 ? skillBonuses : editingItem.item.skillBonuses || null,
      savingThrowBonuses: savingThrowBonuses.length > 0 ? savingThrowBonuses : editingItem.item.savingThrowBonuses || null,
      conditionImmunities: conditionImmunities.length > 0 ? conditionImmunities : editingItem.item.conditionImmunities || null,
      darkvision: darkvision ? parseInt(darkvision) : editingItem.item.darkvision || null,
      blindsight: blindsight ? parseInt(blindsight) : editingItem.item.blindsight || null,
      tremorsense: tremorsense ? parseInt(tremorsense) : editingItem.item.tremorsense || null,
      advantageOn: advantageOn.length > 0 ? advantageOn : editingItem.item.advantageOn || null,
      // [D.3 APR 2026]: Persist edited grid footprint. Clamped 1-4 per axis.
      size: [
        Math.max(1, Math.min(4, parseInt(itemWidth, 10) || 1)),
        Math.max(1, Math.min(4, parseInt(itemHeight, 10) || 1))
      ],
      // [P2.13 APR 29 2026]: Persist stack quantity for stackable items only.
      // [WHY]: Non-stackable items have no stack semantics — leave their
      //   quantity untouched (default 1 in InventoryContext.addItem).
      // [PASS-D APR 30 2026]: Use local `stackable` state (the toggle) so
      //   flipping the toggle ON for a previously-non-stackable item also
      //   persists the quantity entered.
      ...(stackable
        ? { quantity: Math.max(1, Math.min(999, parseInt(quantity, 10) || 1)) }
        : {}),
      // [PASS-D APR 30 2026]: Persist the stackable flag. Per-item user
      //   override of the BASE_ITEMS default — players can flip a Rogue's
      //   throwing daggers on, or flip a unique-spell scroll off, etc.
      stackable: stackable,
      // [FIX MAY 2026]: Persist maxStack as per-item override. null when
      //   the field is empty so addItem/mergeStackOnto fall back to the
      //   BASE_ITEMS default. parseInt guards against invalid input.
      maxStack: stackable && maxStack !== '' && Number.isFinite(parseInt(maxStack, 10))
        ? Math.max(1, Math.min(999, parseInt(maxStack, 10)))
        : null,
    };
    
    // Add container-specific properties if this is a container
    if (editingItem.item.isContainer) {
      updates.containerSlots = [
        Math.max(2, Math.min(12, parseInt(containerWidth) || 4)),
        Math.max(2, Math.min(12, parseInt(containerHeight) || 4))
      ];
    }
    
    // [PHASE 6A]: Capture old item properties for change tracking
    const oldItem = { ...editingItem.item };

    updateItem(editingItem.item.id, updates, editingItem.source);

    // [PHASE 6A]: Emit ITEM_EDITED event with comprehensive change tracking
    systemBus.emit(EVENTS.ITEM_EDITED, {
      item: { ...oldItem, ...updates },
      itemId: editingItem.item.id,
      oldProperties: oldItem,
      newProperties: updates,
      source: editingItem.source,
      changes: {
        name: oldItem.name !== updates.name,
        description: oldItem.description !== updates.description,
        weight: oldItem.weight !== updates.weight,
        value: oldItem.value !== updates.value,
        charges: oldItem.charges !== updates.charges,
        maxCharges: oldItem.maxCharges !== updates.maxCharges,
        customImage: oldItem.customImage !== updates.customImage,
        statModifications: JSON.stringify(oldItem.statModifications) !== JSON.stringify(updates.statModifications),
        requiresAttunement: oldItem.requiresAttunement !== updates.requiresAttunement
      }
    });

    setEditingItem(null);
  };
  
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  return (
    <MobileModal
      isOpen={!!editingItem}
      onClose={() => setEditingItem(null)}
      title="Edit Item"
      onSave={handleSave}
      saveLabel="Save Changes"
      data-testid="edit-item-dialog"
    >
        
        <div className="mb-6">
          <label className="block text-sm text-gray-300 mb-2">Name</label>
          <input 
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-3 bg-gray-800 text-white rounded-lg text-base min-h-[48px] border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm text-gray-300 mb-2">Description</label>
          <textarea 
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-3 bg-gray-800 text-white rounded-lg text-base min-h-[120px] border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors resize-none"
            rows={4}
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm text-gray-300 mb-2">Weight (lbs)</label>
          <input 
            type="number"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="w-full p-3 bg-gray-800 text-white rounded-lg text-base min-h-[48px] border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm text-gray-300 mb-2">Value (gp)</label>
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full p-3 bg-gray-800 text-white rounded-lg text-base min-h-[48px] border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          />
        </div>

        {/* [DELETED MAY 2026]: Top-level "Stack Behavior" amber fieldset
            removed. Stackable toggle + Stack Quantity + Max Stack now live
            inside Advanced Properties (below) per user request. The bespoke
            amber-bordered prominent fieldset competed with the more
            important fields (Name / Value / Weight / Description). Stack
            controls are an opt-in tweak, not a primary attribute. */}

        {/* [D.3 APR 2026]: Grid footprint editor. Width × height in cells. */}
        <div className="mb-6">
          <label className="block text-sm text-gray-300 mb-2">Grid footprint (cells)</label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={4}
              step={1}
              value={itemWidth}
              onChange={(e) => setItemWidth(Math.max(1, Math.min(4, parseInt(e.target.value, 10) || 1)))}
              className="w-20 p-2 bg-gray-800 text-white rounded border border-gray-700"
              aria-label="Grid width"
            />
            <span className="text-gray-500">×</span>
            <input
              type="number"
              min={1}
              max={4}
              step={1}
              value={itemHeight}
              onChange={(e) => setItemHeight(Math.max(1, Math.min(4, parseInt(e.target.value, 10) || 1)))}
              className="w-20 p-2 bg-gray-800 text-white rounded border border-gray-700"
              aria-label="Grid height"
            />
            <span className="text-xs text-gray-500">
              {itemWidth} × {itemHeight} cells (1–4 each)
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            How much inventory grid space this item occupies. Move the item outside any container first if the new size would overflow.
          </p>
        </div>

        {/* Recharge Type */}
        {(editingItem.item.charges > 0 || editingItem.item.maxCharges > 0) && (
          <div className="mb-6">
            <label className="block text-sm text-gray-300 mb-2">Recharge Type</label>
            <select
              value={rechargeType}
              onChange={(e) => setRechargeType(e.target.value)}
              className="w-full p-3 bg-gray-800 text-white rounded-lg text-base min-h-[48px] border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            >
              <option value={RECHARGE_TYPES.NONE}>No Recharge</option>
              <option value={RECHARGE_TYPES.SHORT_REST}>Short Rest</option>
              <option value={RECHARGE_TYPES.LONG_REST}>Long Rest</option>
              <option value={RECHARGE_TYPES.DAWN}>Dawn</option>
              <option value={RECHARGE_TYPES.DAILY}>Daily (24 hours)</option>
              <option value={RECHARGE_TYPES.WEEKLY}>Weekly</option>
              <option value={RECHARGE_TYPES.MONTHLY}>Monthly</option>
              <option value={RECHARGE_TYPES.CONSUMABLE}>Consumable (No Recharge)</option>
              <option value={RECHARGE_TYPES.SPECIAL}>Special Conditions</option>
            </select>
            {rechargeType === RECHARGE_TYPES.SPECIAL && (
              <p className="text-xs text-gray-400 mt-1">Describe special recharge conditions in the item description</p>
            )}
          </div>
        )}
        
        {hasPremium && (
          <div className="mb-4">
            <label className="block text-sm text-gray-300 mb-1">Custom Image (Premium)</label>
            {customImage && (
              <div className="mb-2 p-2 bg-gray-800 rounded">
                <img src={customImage} alt="Custom" className="w-16 h-16 mx-auto object-contain" />
              </div>
            )}

            {/* [v1.0]: AI Icon Generator */}
            {showAIGenerator ? (
              <ChunkLoadErrorBoundary>
                <Suspense fallback={<div className="p-4 bg-gray-800 rounded text-gray-400 text-sm text-center">Loading AI Generator...</div>}>
                  <AIImageGenerator
                    mode="icon"
                    onImageGenerated={(img) => {
                      setCustomImage(img);
                      setShowAIGenerator(false);
                    }}
                    onClose={() => setShowAIGenerator(false)}
                    onSaveToGallery={(entry) => {
                      // [AUDIT #100 — 2026-05-18]: Save to playerStats.imageLibrary
                      //   instead of legacy character.imageGallery. Entity-bound to
                      //   this item so the gallery picker can filter back via
                      //   imagesByEntity.
                      const itemId = editingItem?.item?.id || editingItem?.item?.key || name || '';
                      const data = entry?.data || entry?.image || entry?.url || '';
                      const entryName = entry?.name || entry?.label || entry?.prompt || `${name || 'Item'} icon`;
                      if (!data) return;
                      setCharacter(addImage({
                        kind: 'item',
                        name: entryName,
                        data,
                        tags: ['ai-generated'],
                        ...(itemId ? { entityId: String(itemId), entityKind: 'item' } : {}),
                      }));
                    }}
                    onLogActivity={logActivity}
                    defaultParams={{
                      itemName: name || editingItem?.item?.name || 'Item',
                      itemType: editingItem?.item?.category || '',
                      description: description || '',
                    }}
                  />
                </Suspense>
              </ChunkLoadErrorBoundary>
            ) : (
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => setShowAIGenerator(true)}
                  className="flex items-center gap-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded transition-colors"
                >
                  <Sparkles size={14} />
                  Generate Icon (AI)
                </button>
                <button
                  onClick={() => setShowGallery(true)}
                  className="flex items-center gap-1 px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded transition-colors"
                  >
                    <Image size={14} />
                    Gallery ({(() => {
                      // [AUDIT #100 — 2026-05-18]: count legacy + new library entries
                      const itemId = editingItem?.item?.id || editingItem?.item?.key || name || '';
                      const legacy = character?.imageGallery || [];
                      if (!itemId) return legacy.length;
                      const fromLibrary = imagesByEntity(character, itemId, 'item');
                      const seen = new Set(legacy.map((g) => g.id));
                      const novel = fromLibrary.filter((g) => !seen.has(g.id));
                      return legacy.length + novel.length;
                    })()})
                  </button>
              </div>
            )}

            {/* [v1.1]: Image Gallery modal for item icons */}
            {showGallery && (
              <ChunkLoadErrorBoundary>
                <Suspense fallback={null}>
                  <ImageGallery
                    images={(() => {
                      // [AUDIT #100 — 2026-05-18]: merge legacy imageGallery + new
                      //   entity-bound imageLibrary entries for the picker.
                      const itemId = editingItem?.item?.id || editingItem?.item?.key || name || '';
                      const legacy = character?.imageGallery || [];
                      if (!itemId) return legacy;
                      const fromLibrary = imagesByEntity(character, itemId, 'item');
                      const seen = new Set(legacy.map((g) => g.id));
                      const novel = fromLibrary.filter((g) => !seen.has(g.id));
                      return [...legacy, ...novel];
                    })()}
                    initialFilter="icon"
                    onSelectImage={(img) => setCustomImage(img)}
                    onDeleteImage={(imageId) => {
                      setCharacter(prev => ({
                        ...prev,
                        imageGallery: (prev.imageGallery || []).filter(i => i.id !== imageId),
                      }));
                    }}
                    onClose={() => setShowGallery(false)}
                  />
                </Suspense>
              </ChunkLoadErrorBoundary>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full p-2 bg-gray-800 text-white rounded text-sm"
            />
            {customImage && (
              <button
                onClick={() => setCustomImage('')}
                className="mt-2 text-red-400 text-sm hover:text-red-300"
              >
                Remove Image
              </button>
            )}
          </div>
        )}
        
        {/* Container-specific properties */}
        {editingItem.item.isContainer && (
          <div className="mb-4 p-3 bg-gradient-to-r from-amber-900 to-orange-900 rounded border border-amber-600">
            <h4 className="text-sm font-bold text-amber-300 mb-3 flex items-center">
              🎒 Container Properties
              <span className="ml-2 text-xs bg-amber-800 px-2 py-1 rounded">
                {editingItem.item.containerSize}
              </span>
            </h4>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm text-amber-200 block mb-2">Internal Grid Dimensions</label>
                <div className="flex gap-2 items-center">
                  <input 
                    type="number"
                    min="2"
                    max="12"
                    value={containerWidth}
                    onChange={(e) => setContainerWidth(Math.max(2, Math.min(12, parseInt(e.target.value) || 4)))}
                    className="w-16 p-2 bg-gray-800 text-white rounded text-center"
                    title="Container width (2-12)"
                  />
                  <span className="text-amber-400">×</span>
                  <input 
                    type="number"
                    min="2"
                    max="12"
                    value={containerHeight}
                    onChange={(e) => setContainerHeight(Math.max(2, Math.min(12, parseInt(e.target.value) || 4)))}
                    className="w-16 p-2 bg-gray-800 text-white rounded text-center"
                    title="Container height (2-12)"
                  />
                  <div className="text-xs text-amber-300 ml-2">
                    = {containerWidth * containerHeight} slots
                  </div>
                </div>
                <div className="text-xs text-amber-200 mt-1">
                  💡 Current: {editingItem.item.containerSlots?.[0] || 4}×{editingItem.item.containerSlots?.[1] || 4} 
                  → New: {containerWidth}×{containerHeight}
                </div>
              </div>
              
              <div className="bg-amber-800 p-2 rounded text-xs text-amber-100">
                <strong>🧙‍♂️ Magical Containers:</strong> Configure dimensions to simulate special containers:
                <div className="mt-1 space-y-1">
                  <div>• <strong>Bag of Holding:</strong> 10×10 or larger</div>
                  <div>• <strong>Portable Hole:</strong> 12×12 (maximum)</div>
                  <div>• <strong>Handy Haversack:</strong> 8×6</div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {!editingItem.item.isContainer && (
          <div className="mb-4">
            <label className="block text-sm text-gray-300 mb-1">Charges</label>
            <div className="flex gap-2">
              <input 
                type="number"
                value={charges}
                onChange={(e) => setCharges(e.target.value)}
                placeholder="Current"
                className="flex-1 p-2 bg-gray-800 text-white rounded"
              />
              <input 
                type="number"
                value={maxCharges}
                onChange={(e) => setMaxCharges(e.target.value)}
                placeholder="Max"
                className="flex-1 p-2 bg-gray-800 text-white rounded"
              />
            </div>
          </div>
        )}
        
        {/* Advanced Properties Section */}
        <div className="mb-4 bg-gradient-to-r from-purple-900 to-blue-900 p-3 rounded border border-purple-600">
          <button
            type="button"
            onClick={() => setAdvancedExpanded(!advancedExpanded)}
            className="w-full flex items-center justify-between text-purple-300 hover:text-purple-100"
          >
            <span className="flex items-center">
              <Sparkles size={16} className="mr-2" />
              <span className="font-bold">⚒️ Advanced Properties</span>
            </span>
            {advancedExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
          
          {advancedExpanded && (
            <div className="mt-3 space-y-3">
              {/* [FIX MAY 2026]: Stack Behavior — moved here from a top-level
                  amber fieldset. Three controls: Stackable toggle, Stack
                  Quantity (when stackable), Max Stack Size (when stackable).
                  All three persist as per-instance overrides of the BASE_ITEMS
                  defaults. */}
              <div className="border-b border-purple-700/40 pb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    aria-label="Stackable"
                    checked={stackable}
                    onChange={(e) => setStackable(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-purple-200 font-medium">Stackable</span>
                </label>
                <p className="text-xs text-purple-300/70 mt-1 ml-6">
                  Allow this item to combine with identical items into one stack.
                </p>

                {stackable && (
                  <div className="mt-3 ml-6 space-y-2">
                    <div>
                      <label htmlFor="adv-stack-quantity" className="text-xs text-purple-200 block">
                        Stack Quantity
                      </label>
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          type="button"
                          aria-label="Decrease quantity"
                          onClick={() =>
                            setQuantity(prev => Math.max(1, (parseInt(prev, 10) || 1) - 1))
                          }
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded min-w-[44px] font-bold transition-colors"
                        >
                          −
                        </button>
                        <input
                          id="adv-stack-quantity"
                          type="number"
                          min={1}
                          max={999}
                          step={1}
                          value={quantity}
                          aria-label="Stack quantity"
                          onChange={(e) =>
                            setQuantity(Math.max(1, Math.min(999, parseInt(e.target.value, 10) || 1)))
                          }
                          className="flex-1 p-2 bg-gray-800 text-white rounded text-center"
                        />
                        <button
                          type="button"
                          aria-label="Increase quantity"
                          onClick={() =>
                            setQuantity(prev => Math.min(999, (parseInt(prev, 10) || 1) + 1))
                          }
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded min-w-[44px] font-bold transition-colors"
                        >
                          +
                        </button>
                      </div>
                      <p className="text-xs text-purple-300/70 mt-1">
                        Current count in this stack (use Remove to delete entirely).
                      </p>
                    </div>
                    <div>
                      <label htmlFor="adv-max-stack" className="text-xs text-purple-200 block">
                        Max Stack Size
                      </label>
                      <input
                        id="adv-max-stack"
                        type="number"
                        min={1}
                        max={999}
                        step={1}
                        value={maxStack}
                        placeholder="(default)"
                        aria-label="Max stack size"
                        onChange={(e) => setMaxStack(e.target.value)}
                        className="w-full p-2 bg-gray-800 text-white rounded mt-1"
                      />
                      <p className="text-xs text-purple-300/70 mt-1">
                        Cap for how many can fit in one stack. Leave blank to use the
                        item's default (e.g. arrows: 20, potions: 99).
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Material */}
              <div>
                <label className="text-sm text-purple-200">Material</label>
                <select
                  value={material}
                  onChange={(e) => setMaterial(e.target.value)}
                  className="w-full p-2 bg-gray-800 text-white rounded mt-1"
                >
                  <option value="">Standard</option>
                  <option value="adamantine">Adamantine</option>
                  <option value="mithral">Mithral</option>
                  <option value="silvered">Silvered</option>
                  <option value="cold-iron">Cold Iron</option>
                  <option value="dragonscale">Dragonscale</option>
                  <option value="darkwood">Darkwood</option>
                </select>
              </div>
              
              {/* Equipment Slot */}
              {/* [SUB-PLAN 1R-eid — 2026-05-29]: Replaced inline <select> with
                  <ReferencePicker kind="equipSlot">. The composer registry now
                  owns the 26-slot vocabulary (enums.js EQUIPMENT_SLOT_LABELS)
                  so this picker automatically includes 'ioun', 'misc', and any
                  future slots added to enums.js without touching EditItemDialog.
                  Pre-fix: hand-rolled <select> iterating EQUIPMENT_SLOT_LABELS
                  inline — same data source but bypassing the composer substrate.
                  "None (Not Equippable)" is emitted as placeholder when value
                  is falsy (ReferencePicker renders placeholder text for '').
                  Parity: matches ItemDialog Stone 2 (1T commit 6f501b8). */}
              <div>
                <label className="text-sm text-purple-200">Equipment Slot</label>
                <ReferencePicker
                  kind="equipSlot"
                  value={equipSlot || ''}
                  onChange={setEquipSlot}
                  placeholder="None (Not Equippable)"
                />
              </div>
              
              {/* Requires Attunement */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requiresAttunement"
                  checked={requiresAttunement}
                  onChange={(e) => setRequiresAttunement(e.target.checked)}
                  className="mr-2 rounded"
                />
                <label htmlFor="requiresAttunement" className="text-sm text-purple-200">
                  Requires Attunement
                </label>
              </div>
              
              {/* Magic Bonus */}
              <div>
                <label className="text-sm text-purple-200">Magic Bonus</label>
                <select
                  value={magicBonus}
                  onChange={(e) => setMagicBonus(e.target.value)}
                  className="w-full p-2 bg-gray-800 text-white rounded mt-1"
                >
                  <option value="">None</option>
                  <option value="+1">+1</option>
                  <option value="+2">+2</option>
                  <option value="+3">+3</option>
                  <option value="+4">+4 (Legendary)</option>
                  <option value="+5">+5 (Artifact)</option>
                </select>
              </div>
              
              {/* Damage Override */}
              <div>
                <label className="text-sm text-purple-200">Damage Dice Override</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="text"
                    value={damageDice}
                    onChange={(e) => setDamageDice(e.target.value)}
                    placeholder="e.g. 2d6"
                    className="flex-1 p-2 bg-gray-800 text-white rounded"
                  />
                  <select
                    value={damageType}
                    onChange={(e) => setDamageType(e.target.value)}
                    className="p-2 bg-gray-800 text-white rounded"
                  >
                    <option value="">Type</option>
                    <option value="slashing">Slashing</option>
                    <option value="piercing">Piercing</option>
                    <option value="bludgeoning">Bludgeoning</option>
                    <option value="fire">Fire</option>
                    <option value="cold">Cold</option>
                    <option value="lightning">Lightning</option>
                    <option value="acid">Acid</option>
                    <option value="poison">Poison</option>
                    <option value="necrotic">Necrotic</option>
                    <option value="radiant">Radiant</option>
                    <option value="psychic">Psychic</option>
                    <option value="force">Force</option>
                  </select>
                </div>
              </div>
              
              {/* Spell Granting */}
              {/* [BATCH 11 / P1 D-28 — APR 2026]: Sourced from SRD_SPELLS +
                  sourcesManager via spellsByLevel useMemo (343 SRD + active
                  source spells). Pre-fix the dropdown was a hardcoded
                  ~120-spell SPELL_LIST, levels 0-5 only. */}
              <div>
                <label className="text-sm text-purple-200">Grant Spell</label>
                <select
                  value={selectedSpell}
                  onChange={(e) => setSelectedSpell(e.target.value)}
                  className="w-full p-2 bg-gray-800 text-white rounded mt-1"
                >
                  <option value="">No Spell</option>
                  {[
                    ['Cantrips', 0],
                    ['1st Level', 1],
                    ['2nd Level', 2],
                    ['3rd Level', 3],
                    ['4th Level', 4],
                    ['5th Level', 5],
                    ['6th Level', 6],
                    ['7th Level', 7],
                    ['8th Level', 8],
                    ['9th Level', 9],
                  ].map(([label, lvl]) => (
                    spellsByLevel[lvl] && spellsByLevel[lvl].length > 0 && (
                      <optgroup key={lvl} label={label}>
                        {spellsByLevel[lvl].map(spell => (
                          <option key={spell.name} value={spell.name}>{spell.name}</option>
                        ))}
                      </optgroup>
                    )
                  ))}
                </select>
                {selectedSpell && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="number"
                      value={spellCharges}
                      onChange={(e) => setSpellCharges(e.target.value)}
                      placeholder="Uses"
                      className="w-20 p-2 bg-gray-800 text-white rounded"
                    />
                    <select
                      value={spellRecharge}
                      onChange={(e) => setSpellRecharge(e.target.value)}
                      className="flex-1 p-2 bg-gray-800 text-white rounded"
                    >
                      <option value="dawn">Recharges at Dawn</option>
                      <option value="dusk">Recharges at Dusk</option>
                      <option value="short-rest">Short Rest</option>
                      <option value="long-rest">Long Rest</option>
                      <option value="never">One-time Use</option>
                    </select>
                  </div>
                )}
              </div>
              
              {/* [ENHANCED]: Multi-Stat Modifications */}
              {/* [WHY]: Support items like Cloak of Protection (+1 AC, +1 DEX, +1 saves) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-purple-200 font-bold block">
                    ✨ Ability Score Modifications
                  </label>
                  {/* [PHASE B / #029 — 2026-05-06]: Cursed Preset (B1-3).
                      One-click structured cursed-item shape: flips
                      isCursed ON and seeds a -1 STR (add) row in the
                      stat-mod grid. Author can refine the value, swap
                      the ability, or layer additional cursed effects
                      via FeatureEffectsSection. The free-text
                      curseDescription field stays available for
                      narrative flavor (no overwrite). Idempotent:
                      clicking twice still yields -1 STR, not -2 — the
                      preset writes a definite shape, not a delta. */}
                  <button
                    type="button"
                    onClick={applyCursedPreset}
                    className="text-xs px-2 py-1 bg-red-900/50 hover:bg-red-800/60 text-red-200 rounded border border-red-700/40 transition-colors"
                    title="Cursed Preset — flips isCursed ON and seeds -1 STR"
                  >
                    🩸 Cursed Preset
                  </button>
                </div>
                <div className="text-xs text-purple-300 mb-3">
                  Configure multiple stat modifications per item (SET overrides, ADD gives bonus)
                </div>

                {/* Ability Score Grid */}
                <div className="space-y-2">
                  {Object.entries(statMods).map(([ability, mod]) => {
                    const abilityLabel = ability.charAt(0).toUpperCase() + ability.slice(1);
                    const abilityShort = ability.substring(0, 3).toUpperCase();

                    return (
                      <div key={ability} className="bg-gray-800 p-2 rounded border border-gray-700">
                        {/* Checkbox and Ability Name */}
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            id={`stat-${ability}`}
                            checked={mod.enabled}
                            onChange={(e) => {
                              setStatMods(prev => ({
                                ...prev,
                                [ability]: { ...prev[ability], enabled: e.target.checked }
                              }));
                            }}
                            className="rounded"
                          />
                          <label
                            htmlFor={`stat-${ability}`}
                            className="text-sm text-purple-200 font-semibold flex-1 cursor-pointer"
                          >
                            {abilityLabel} ({abilityShort})
                          </label>
                        </div>

                        {/* Effect Type and Value (only when enabled) */}
                        {mod.enabled && (
                          <div className="flex gap-2 ml-6">
                            {/* SET or ADD radio buttons */}
                            <div className="flex gap-2 items-center">
                              <label className="flex items-center gap-1 text-xs text-purple-300 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`${ability}-type`}
                                  value="set"
                                  checked={mod.type === 'set'}
                                  onChange={(e) => {
                                    setStatMods(prev => ({
                                      ...prev,
                                      [ability]: { ...prev[ability], type: 'set' }
                                    }));
                                  }}
                                  className="rounded-full"
                                />
                                SET
                              </label>
                              <label className="flex items-center gap-1 text-xs text-purple-300 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`${ability}-type`}
                                  value="add"
                                  checked={mod.type === 'add'}
                                  onChange={(e) => {
                                    setStatMods(prev => ({
                                      ...prev,
                                      [ability]: { ...prev[ability], type: 'add' }
                                    }));
                                  }}
                                  className="rounded-full"
                                />
                                ADD
                              </label>
                            </div>

                            {/* Value Input */}
                            <input
                              type="number"
                              value={mod.value}
                              onChange={(e) => {
                                setStatMods(prev => ({
                                  ...prev,
                                  [ability]: { ...prev[ability], value: e.target.value }
                                }));
                              }}
                              placeholder={mod.type === 'set' ? "1-30" : "±10"}
                              min={mod.type === 'set' ? "1" : "-10"}
                              max="30"
                              className="flex-1 p-1.5 bg-gray-900 text-white rounded text-sm"
                            />

                            {/* [SUB-PLAN 1H-H-maxcap-authoring — 2026-05-30]: Ability score ceiling.
                                Shown only for ADD operations — SET targets a specific value (no ceiling
                                concept). Allows authoring "+2 DEX to a maximum of 20" (Ioun Stone semantics).
                                Fork resolution Option B: gated on ability-score add operations only. */}
                            {mod.type === 'add' && (
                              <div className="flex items-center gap-1">
                                <label
                                  htmlFor={`stat-${ability}-max`}
                                  className="text-xs text-amber-300 whitespace-nowrap"
                                >
                                  max
                                </label>
                                <input
                                  type="number"
                                  id={`stat-${ability}-max`}
                                  data-testid={`stat-${ability}-max`}
                                  value={mod.max !== null && mod.max !== undefined ? mod.max : ''}
                                  onChange={(e) => {
                                    setStatMods(prev => ({
                                      ...prev,
                                      [ability]: {
                                        ...prev[ability],
                                        max: e.target.value === '' ? null : Number(e.target.value),
                                      }
                                    }));
                                  }}
                                  placeholder="e.g. 20"
                                  min="1"
                                  max="30"
                                  title="Ability score ceiling (e.g. 20 for the default D&D 5e cap)"
                                  className="w-16 p-1.5 bg-gray-900 text-white rounded text-sm border border-amber-700/60 focus:border-amber-400 outline-none"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* D&D 5e Examples */}
                <div className="mt-3 bg-blue-900/30 p-2 rounded text-xs">
                  <div className="text-blue-200 font-bold mb-1">📖 D&D 5e Examples:</div>
                  <div className="text-blue-300 space-y-0.5">
                    <div>• <strong>Belt of Giant Strength:</strong> STR SET to 21/23/25/27/29</div>
                    <div>• <strong>Amulet of Health:</strong> CON SET to 19</div>
                    <div>• <strong>Headband of Intellect:</strong> INT SET to 19</div>
                    <div>• <strong>Gauntlets of Ogre Power:</strong> STR SET to 19</div>
                    <div>• <strong>Cloak of Protection:</strong> No stat mods (just +1 AC, +1 saves)</div>
                  </div>
                </div>
              </div>

              {/* [SUB-PLAN 1N — 2026-05-27]: HP / AC / Initiative / Saves bonus fields.
                  Writes to canonical S1 effects[] only — NOT to statModifications S4. */}
              <div>
                <label className="text-sm text-purple-200 font-bold block mb-2">
                  Modifiers
                </label>
                <div className="flex gap-3 mb-2">
                  <div>
                    <label className="block text-xs text-purple-300 mb-1">HP Bonus</label>
                    <input
                      type="number"
                      value={bonusHP}
                      onChange={(e) => setBonusHP(e.target.value)}
                      placeholder="0"
                      style={{ width: '60px' }}
                      className="p-1.5 bg-gray-900 text-white rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-purple-300 mb-1">AC Bonus</label>
                    <input
                      type="number"
                      value={acBonus}
                      onChange={(e) => setAcBonus(e.target.value)}
                      placeholder="0"
                      style={{ width: '60px' }}
                      className="p-1.5 bg-gray-900 text-white rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-purple-300 mb-1">Initiative Bonus</label>
                    <input
                      type="number"
                      value={initiativeBonus}
                      onChange={(e) => setInitiativeBonus(e.target.value)}
                      placeholder="0"
                      style={{ width: '60px' }}
                      className="p-1.5 bg-gray-900 text-white rounded text-sm"
                    />
                  </div>
                </div>
                <div className="text-xs text-purple-300 mb-1 font-semibold">Saving Throw Bonuses</div>
                <div className="grid grid-cols-4 gap-1">
                  {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma', 'all'].map((save) => (
                    <div key={save}>
                      <label className="block text-xs text-purple-300 mb-0.5 capitalize">
                        {save === 'all' ? 'All' : save.slice(0, 3).charAt(0).toUpperCase() + save.slice(1, 3)}
                      </label>
                      <input
                        type="number"
                        value={saves[save] || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSaves(prev => ({ ...prev, [save]: val }));
                        }}
                        placeholder="0"
                        style={{ width: '52px' }}
                        className="p-1.5 bg-gray-900 text-white rounded text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* [PHASE B / #026 — 2026-05-06]: Items become the 6th member
                  of the CW authoring quintet by sharing FeatureEffectsSection.
                  The section is purely presentational — it dispatches to the
                  callbacks bound below to mutate the local `effects` state.
                  On save, the state merges into `updates.effects` alongside
                  the legacy resistance/immunity bridge entries (handleSave
                  above). itemEffectsRegistration consumes the merged array
                  on equip, so the new effect types (multi stat-mod via
                  ac/attackBonus/damageBonus/etc., proficiency grants, sense
                  grants, advantage/resistance, custom feat flags) all
                  register through effectRegistry without item-shape
                  changes elsewhere. */}
              <div className="mt-2">
                <FeatureEffectsSection
                  effects={effects}
                  hasChoices={false}
                  choiceKey={''}
                  choiceOptions={[]}
                  onAddEffect={() => {
                    setEffects(prev => [...prev, emptyEffect()]);
                  }}
                  onRemoveEffect={(idx) => {
                    setEffects(prev => prev.filter((_, j) => j !== idx));
                  }}
                  onUpdateEffect={(idx, field, value) => {
                    setEffects(prev => prev.map((eff, j) =>
                      j === idx ? { ...eff, [field]: value } : eff
                    ));
                  }}
                  // Items don't currently use the choice block — that's a
                  // class/race feature pattern. Stub the choice callbacks
                  // so FeatureEffectsSection's contract is satisfied.
                  onToggleHasChoices={() => {}}
                  onUpdateChoiceKey={() => {}}
                  onAddChoiceOption={() => {}}
                  onRemoveChoiceOption={() => {}}
                  onUpdateChoiceOptionName={() => {}}
                  onAddChoiceOptionEffect={() => {}}
                  onRemoveChoiceOptionEffect={() => {}}
                  onUpdateChoiceOptionEffect={() => {}}
                />
              </div>

              {/* [PHASE 10 - NOV 20, 2025]: RESTORED D&D 5e PROPERTIES UI */}
              {/* [WHY]: Allow editing of all D&D 5e magic item properties for SRD recreation */}

              {/* Rarity */}
              <div>
                <label className="text-sm text-purple-200">Item Rarity</label>
                <select
                  value={rarity}
                  onChange={(e) => setRarity(e.target.value)}
                  className="w-full p-2 bg-gray-800 text-white rounded mt-1"
                >
                  <option value="">No Rarity (Mundane)</option>
                  <option value="common">Common</option>
                  <option value="uncommon">Uncommon</option>
                  <option value="rare">Rare</option>
                  <option value="very_rare">Very Rare</option>
                  <option value="legendary">Legendary</option>
                  <option value="artifact">Artifact</option>
                </select>
              </div>

              {/* Attunement Prerequisite (only if attunement required) */}
              {requiresAttunement && (
                <div>
                  <label className="text-sm text-purple-200">Attunement Prerequisite</label>
                  <input
                    type="text"
                    value={attunementPrerequisite}
                    onChange={(e) => setAttunementPrerequisite(e.target.value)}
                    placeholder="e.g., by a spellcaster, by a cleric"
                    className="w-full p-2 bg-gray-800 text-white rounded mt-1 text-sm"
                  />
                </div>
              )}

              {/* [SUB-PLAN 1H-K — 2026-05-29]: Per-instance attunement choices.
                  Rendered only when the BASE_ITEMS def has a customChoices config.
                  One ReferencePicker per field — uses the registered kind so the
                  picker catalog stays in sync with composerCatalogRegistry.
                  On pick: update customChoices state; effects are materialized at save. */}
              {_baseEntry && _baseEntry.customChoices && (
                <div className="mt-2">
                  <p className="text-xs text-amber-300 mb-2 font-medium">Attunement Choices</p>
                  {Object.entries(_baseEntry.customChoices).map(([fieldKey, choiceConfig]) => (
                    <div key={fieldKey} className="mb-2">
                      <label className="text-sm text-purple-200 block mb-1">
                        {choiceConfig.label || fieldKey}
                      </label>
                      <ReferencePicker
                        kind={choiceConfig.kind}
                        value={customChoices[fieldKey] || null}
                        onChange={(picked) => setCustomChoices(prev => ({ ...prev, [fieldKey]: picked }))}
                        placeholder="-- Choose --"
                        playerStats={character}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Cursed Item */}
              <div>
                <label className="flex items-center text-sm text-red-300 mb-1">
                  <input
                    type="checkbox"
                    checked={isCursed}
                    onChange={(e) => setIsCursed(e.target.checked)}
                    className="mr-2 rounded"
                  />
                  Cursed Item
                </label>
                {isCursed && (
                  <textarea
                    value={curseDescription}
                    onChange={(e) => setCurseDescription(e.target.value)}
                    placeholder="Describe the curse and its effects..."
                    rows="2"
                    className="w-full p-2 bg-gray-800 text-white rounded text-sm mt-1"
                  />
                )}
              </div>

              {/* Resistance/Immunity Grants */}
              {/* [BATCH 11 / P1 D-28 — APR 2026]: Replaced the legacy
                  multi-select listbox + modifier-key-required hint with
                  checkbox grids. The previous shape was hostile on touch
                  devices and awkward for keyboard users. Checkboxes are
                  touch-, keyboard-, and screen-reader-friendly. */}
              <div>
                <label className="text-sm text-purple-200 font-bold mb-2 block">
                  🛡️ Damage Resistance/Immunity
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-green-200 mb-1">Grants Resistance To:</label>
                    <div className="grid grid-cols-2 gap-1 p-2 bg-gray-800 rounded">
                      {['fire','cold','lightning','acid','poison','necrotic','radiant','psychic','slashing','piercing','bludgeoning'].map(damageType => (
                        <label key={`res-${damageType}`} className="flex items-center text-xs text-white cursor-pointer">
                          <input
                            type="checkbox"
                            checked={resistances.includes(damageType)}
                            onChange={(e) => {
                              setResistances(prev => e.target.checked
                                ? [...prev, damageType]
                                : prev.filter(t => t !== damageType));
                            }}
                            className="mr-1"
                            data-testid={`resistance-${damageType}`}
                          />
                          <span className="capitalize">{damageType}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-green-200 mb-1">Grants Immunity To:</label>
                    <div className="grid grid-cols-2 gap-1 p-2 bg-gray-800 rounded">
                      {['fire','cold','lightning','acid','poison','necrotic','radiant','psychic','slashing','piercing','bludgeoning'].map(damageType => (
                        <label key={`imm-${damageType}`} className="flex items-center text-xs text-white cursor-pointer">
                          <input
                            type="checkbox"
                            checked={immunities.includes(damageType)}
                            onChange={(e) => {
                              setImmunities(prev => e.target.checked
                                ? [...prev, damageType]
                                : prev.filter(t => t !== damageType));
                            }}
                            className="mr-1"
                            data-testid={`immunity-${damageType}`}
                          />
                          <span className="capitalize">{damageType}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Speed Modifiers */}
              <div>
                <label className="text-sm text-purple-200 font-bold mb-2 block">
                  🏃 Speed Bonuses
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-blue-200 mb-1">Speed Bonus (ft)</label>
                    <input
                      type="number"
                      value={speedBonus}
                      onChange={(e) => setSpeedBonus(e.target.value)}
                      placeholder="e.g., 10"
                      className="w-full p-2 bg-gray-800 text-white rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-blue-200 mb-1">Flying Speed (ft)</label>
                    <input
                      type="number"
                      value={flyingSpeed}
                      onChange={(e) => setFlyingSpeed(e.target.value)}
                      placeholder="e.g., 60"
                      className="w-full p-2 bg-gray-800 text-white rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-blue-200 mb-1">Swimming Speed (ft)</label>
                    <input
                      type="number"
                      value={swimmingSpeed}
                      onChange={(e) => setSwimmingSpeed(e.target.value)}
                      placeholder="e.g., 40"
                      className="w-full p-2 bg-gray-800 text-white rounded text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Senses */}
              <div>
                <label className="text-sm text-purple-200 font-bold mb-2 block">
                  👁️ Senses
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-indigo-200 mb-1">Darkvision (ft)</label>
                    <input
                      type="number"
                      value={darkvision}
                      onChange={(e) => setDarkvision(e.target.value)}
                      placeholder="e.g., 60"
                      className="w-full p-2 bg-gray-800 text-white rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-indigo-200 mb-1">Blindsight (ft)</label>
                    <input
                      type="number"
                      value={blindsight}
                      onChange={(e) => setBlindsight(e.target.value)}
                      placeholder="e.g., 30"
                      className="w-full p-2 bg-gray-800 text-white rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-indigo-200 mb-1">Tremorsense (ft)</label>
                    <input
                      type="number"
                      value={tremorsense}
                      onChange={(e) => setTremorsense(e.target.value)}
                      placeholder="e.g., 60"
                      className="w-full p-2 bg-gray-800 text-white rounded text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Advantage Situations — [EID-ADVANTAGE-UI — 2026-06-13]:
                  Parity with ItemDialog (SUB-PLAN 1H-C-ui — 2026-05-28).
                  advantageOn state, load, and save were already wired
                  (lines 201, 343, 658) but no UI existed to populate it.
                  ReferencePicker(kind="advantageTarget") mirrors the
                  ItemDialog control so editing an existing item can set
                  the same advantage grants that authoring supports.
                  Value flows: advantageOn state → handleSave →
                  buildEffectsFromForm → effectRegistry →
                  effectCalculator.getAdvantageTargets(). */}
              <div>
                <label className="block text-xs text-blue-200 mb-1">Grants Advantage On:</label>
                <ReferencePicker
                  kind="advantageTarget"
                  multi
                  value={Array.isArray(advantageOn) ? advantageOn : []}
                  onChange={setAdvantageOn}
                  chipVariant="blue"
                  placeholder="Add a roll this item grants advantage on..."
                />
              </div>

              {/* [PHASE B / #064 — 2026-05-07]: Weapon Properties picker
                  parity with ItemDialog (#030). Iterates the shared
                  WEAPON_PROPERTIES data block — every checkbox carries
                  `title=` (mouse-hover tooltip with the PHB description)
                  and `aria-describedby=` pointing at a hidden description
                  span (screen reader). Range fields (normalRange / longRange)
                  surface here too — they had state + load + save plumbing
                  pre-#064 but no UI to set them. Gated to weapons category
                  to match ItemDialog's gate. */}
              {editingItem?.item?.category === 'weapons' && (
                <div>
                  <h5 className="text-xs font-bold text-red-300 mb-2">⚔️ Weapon Properties</h5>
                  <div className="grid grid-cols-3 gap-2">
                    {WEAPON_PROPERTIES.map(prop => (
                      <label
                        key={prop.id}
                        className="flex items-center text-xs text-white cursor-help"
                        title={prop.description}
                      >
                        <input
                          type="checkbox"
                          checked={weaponProperties.includes(prop.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setWeaponProperties([...weaponProperties, prop.id]);
                            } else {
                              setWeaponProperties(weaponProperties.filter(p => p !== prop.id));
                            }
                          }}
                          aria-describedby={`wp-desc-${prop.id}`}
                          className="mr-1"
                        />
                        {prop.label}
                        <span
                          id={`wp-desc-${prop.id}`}
                          className="sr-only"
                        >
                          {prop.description}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Range fields (for ranged / thrown weapons) */}
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <label className="block text-xs text-red-200 mb-1" htmlFor="edit-normal-range">
                        Normal Range (ft)
                      </label>
                      <input
                        id="edit-normal-range"
                        type="number"
                        value={normalRange}
                        onChange={(e) => setNormalRange(e.target.value)}
                        placeholder="e.g., 80"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-red-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-red-200 mb-1" htmlFor="edit-long-range">
                        Long Range (ft)
                      </label>
                      <input
                        id="edit-long-range"
                        type="number"
                        value={longRange}
                        onChange={(e) => setLongRange(e.target.value)}
                        placeholder="e.g., 320"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-red-600"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* [ENHANCED]: Preview with Stat Modifications */}
              {(() => {
                // Check if any stats are configured
                const hasStatMods = Object.values(statMods).some(mod => mod.enabled && mod.value);
                const showPreview = material || magicBonus || damageDice || selectedSpell || hasStatMods;

                if (!showPreview) return null;

                return (
                  <div className="bg-purple-800 p-2 rounded text-xs">
                    <div className="text-purple-100 font-bold mb-1">✨ Item Preview:</div>
                    <div className="text-purple-200 space-y-0.5">
                      {material && <div>• <strong>Material:</strong> {material}</div>}
                      {magicBonus && <div>• <strong>Enhancement:</strong> {magicBonus}</div>}
                      {damageDice && <div>• <strong>Damage:</strong> {damageDice} {damageType}</div>}
                      {selectedSpell && <div>• <strong>Spell:</strong> {selectedSpell} ({spellCharges || 1}× per {spellRecharge})</div>}
                      {equipSlot && <div>• <strong>Equip Slot:</strong> {equipSlot}</div>}
                      {requiresAttunement && <div>• <strong>Attunement:</strong> Required</div>}

                      {/* Stat Modifications Preview */}
                      {hasStatMods && (
                        <>
                          <div className="text-yellow-300 font-bold mt-1">Stat Modifications:</div>
                          {Object.entries(statMods).map(([ability, mod]) => {
                            if (!mod.enabled || !mod.value) return null;
                            const abilityLabel = ability.charAt(0).toUpperCase() + ability.slice(1);
                            const abilityShort = ability.substring(0, 3).toUpperCase();
                            const value = parseInt(mod.value);

                            if (mod.type === 'set') {
                              return (
                                <div key={ability} className="text-yellow-200">
                                  • <strong>{abilityShort}</strong> SET to <strong>{value}</strong>
                                  {value === 19 && ' (Common Magic Item)'}
                                  {value === 21 && ' (Hill Giant Strength)'}
                                  {value === 23 && ' (Stone/Frost Giant Strength)'}
                                  {value === 25 && ' (Fire Giant Strength)'}
                                  {value === 27 && ' (Cloud Giant Strength)'}
                                  {value === 29 && ' (Storm Giant Strength)'}
                                </div>
                              );
                            } else {
                              return (
                                <div key={ability} className="text-yellow-200">
                                  • <strong>{abilityShort}</strong> {value >= 0 ? '+' : ''}{value} bonus
                                </div>
                              );
                            }
                          })}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        
    </MobileModal>
  );
};

export default EditItemDialog;